import { orderOf } from "../configs/filterOrder";
import type { LeadsFilterOrderAnchor } from "../configs/filterOrder";
import { adSpendChannelsDefinition } from "../models/AdSpendChannelsFilter/definition";
import { adSpendVolumeDefinition } from "../models/AdSpendVolumeFilter/definition";
import { annualRevenueDefinition } from "../models/AnnualRevenueFilter/definition";
import { audienceAgeGroupsDefinition } from "../models/AudienceAgeFilter/definition";
import { audienceGenderSplitsDefinition } from "../models/AudienceGenderFilter/definition";
import { businessModelsDefinition } from "../models/BusinessModelFilter/definition";
import { companyDomainsDefinition } from "../models/CompanyFilter/definition";
import { companyHeadquarterDefinition } from "../models/CompanyHeadquarterFilter/definition";
import { conversionRateDefinition } from "../models/ConversionRateFilter/definition";
import { displayAdsFormatDefinition } from "../models/DisplayAdsFormatFilter/definition";
import { employeeHeadcountDefinition } from "../models/EmployeeHeadcountFilter/definition";
import { engagementDefinition } from "../models/EngagementFilter/definition";
import {
    dynamicsExcludeDefinition,
    hubspotExcludeDefinition,
    salesforceExcludeDefinition,
} from "../models/ExcludeByCrmFilter/definition";
import { staticListsToExcludeDefinition } from "../models/ExcludeByStaticListsFilter/definition";
import { suppressionListIdsDefinition } from "../models/ExcludeBySuppressionListFilter/definition";
import { incomingTrafficDefinition } from "../models/IncomingTrafficFilter/definition";
import { industryCodesFilterDefinition } from "../models/IndustryCodesFilter/definition";
import { industryTaxonomyDefinition } from "../models/IndustryTaxonomyFilter/definition";
import { internationalFiltersDefinition } from "../models/InternationalVisitsFilter/definition";
import { keywordsDefinition } from "../models/KeywordsFilter/definition";
import { leadScoresDefinition } from "../models/LeadScoreFilter/definition";
import { industryFilterDefinition } from "../models/LegacyIndustryFilter/definition";
import { marketingChannelsDefinition } from "../models/MarketingChannelsFilter/definition";
import { monthlyAverageTransactionsDefinition } from "../models/MonthlyAverageTransactionsFilter/definition";
import { onlineRevenueDefinition } from "../models/OnlineRevenueFilter/definition";
import { signalsGroupDefinition } from "../models/SignalsFilter/definition";
import { siteTagsDefinition } from "../models/SiteTagsFilter/definition";
import { technologiesDefinition } from "../models/TechnologyFilter/definition";
import { topGeoCountriesDefinition } from "../models/TopCountryFilter/definition";
import { topLevelDomainDefinition } from "../models/TopLevelDomainsFilter/definition";
import { totalFundingAmountDefinition } from "../models/TotalFundingAmountFilter/definition";
import { usdSpendDefinition } from "../models/UsdSpendFilter/definition";
import { visitsFromDefinition } from "../models/VisitsFromFilter/definition";
import { websiteTypeDefinition } from "../models/WebsiteTypeFilter/definition";
import type { LeadsFilterDefinition, LeadsFilterGroupDefinition } from "../types/leadsFilterDefinition";

/**
 * The leads filters. Display order comes from `filterOrder.ts` (a definition's key is its order anchor), so
 * array position here is not load-bearing — the list is grouped by category only for reading. Signals
 * register as a *group* (ADR-0012 amendment): one entry that fans out into a self-identified card per signal
 * type, anchored at `"signals"`. The CRM custom filters remain a separate descriptor-driven config
 * (`crmFiltersConfig`, ADR-0009), merged in beside this fold.
 */
export const leadsFilterRegistry: readonly (LeadsFilterDefinition | LeadsFilterGroupDefinition)[] = [
    // company
    companyDomainsDefinition,
    businessModelsDefinition,
    employeeHeadcountDefinition,
    leadScoresDefinition,
    annualRevenueDefinition,
    totalFundingAmountDefinition,
    companyHeadquarterDefinition,
    industryFilterDefinition,
    industryTaxonomyDefinition,
    industryCodesFilterDefinition,
    // signals
    signalsGroupDefinition,
    // website
    websiteTypeDefinition,
    technologiesDefinition,
    topLevelDomainDefinition,
    onlineRevenueDefinition,
    monthlyAverageTransactionsDefinition,
    conversionRateDefinition,
    keywordsDefinition,
    siteTagsDefinition,
    // trafficAndEngagement / advertiserActivity
    visitsFromDefinition,
    incomingTrafficDefinition,
    internationalFiltersDefinition,
    topGeoCountriesDefinition,
    engagementDefinition,
    marketingChannelsDefinition,
    usdSpendDefinition,
    audienceGenderSplitsDefinition,
    audienceAgeGroupsDefinition,
    adSpendVolumeDefinition,
    adSpendChannelsDefinition,
    displayAdsFormatDefinition,
    // exclude
    staticListsToExcludeDefinition,
    suppressionListIdsDefinition,
    salesforceExcludeDefinition,
    hubspotExcludeDefinition,
    dynamicsExcludeDefinition,
];

/** A definition's base order — its key is its order anchor. Fanned-out members offset from `orderOfGroupMember`. */
export const orderOfLeadsDefinition = (definition: LeadsFilterDefinition | LeadsFilterGroupDefinition): number =>
    orderOf(definition.key as LeadsFilterOrderAnchor);
